import React from "react";
import { DateTimePicker } from "@mantine/dates";

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onRangeChange: (startDate: Date | null, endDate: Date | null) => void;
  onApplyFilters: () => void;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onRangeChange,
  onApplyFilters,
}) => {
  // Preserve exact time from user input and adjust milliseconds
  const preserveExactTime = (
    date: Date | null,
    milliseconds: number
  ): Date | null => {
    if (!date) return null;

    // Create a new date using UTC time components. If we don't
    // do it this way, daylight saving time can automatically
    // transform the datetime in user-unfriendly ways.
    const newDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      milliseconds
    );

    return newDate;
  };

  const handleStartDateChange = (date: Date | null, shouldApply = false) => {
    const adjustedDate = preserveExactTime(date, 0);

    // Only update if the date is different
    if (
      adjustedDate?.getTime() !== startDate?.getTime() ||
      (adjustedDate === null && startDate !== null)
    ) {
      onRangeChange(adjustedDate, endDate);
      if (shouldApply) {
        onApplyFilters();
      }
    }
  };

  const handleEndDateChange = (date: Date | null, shouldApply = false) => {
    const adjustedDate = preserveExactTime(date, 999);

    if (
      adjustedDate?.getTime() !== endDate?.getTime() ||
      (adjustedDate === null && endDate !== null)
    ) {
      onRangeChange(startDate, adjustedDate);
      if (shouldApply) {
        onApplyFilters();
      }
    }
  };

  return (
    <div className="flex items-center">
      <div className="flex flex-col">
        <DateTimePicker
          placeholder="Start datetime"
          value={startDate}
          onChange={(date) => handleStartDateChange(date, false)}
          clearable
          firstDayOfWeek={0}
          valueFormat="YYYY-MM-DD HH:mm:ss"
          withSeconds
          onBlur={() => {
            handleStartDateChange(startDate, true);
          }}
          submitButtonProps={{
            onClick: () => handleStartDateChange(startDate, true),
          }}
          clearButtonProps={{
            onClick: () => handleStartDateChange(null, true),
          }}
        />
      </div>
      <span className="mx-2">-</span>
      <div className="flex flex-col">
        <DateTimePicker
          placeholder="End datetime"
          value={endDate}
          onChange={(date) => handleEndDateChange(date, false)}
          clearable
          firstDayOfWeek={0}
          valueFormat="YYYY-MM-DD HH:mm:ss"
          withSeconds
          onBlur={() => {
            handleEndDateChange(endDate, true);
          }}
          submitButtonProps={{
            onClick: () => handleEndDateChange(endDate, true),
          }}
          clearButtonProps={{
            onClick: () => handleEndDateChange(null, true),
          }}
        />
      </div>
    </div>
  );
};
